import { NextRouter } from 'next/router'
import { JOURNAL, JSTOR } from '@chegg-wt/core/constants/journalTypes'
import {
  TYPE_CITATIONS,
  TYPE_EXPERT_CHECK,
  TYPE_GRAMMAR,
  TYPE_PLAGIARISM,
} from '@chegg-wt/core/constants/plusTab'
import { TYPE_CREATE_CITATIONS, TYPE_CHECK_PAPER } from '@chegg-wt/core/constants/card'
import { AutociteMetadataInput } from '@chegg-wt/core/globalTypesOneGraph'
import { FetchProjects_projects } from '../queries/FetchProjects'
import { Location } from './upgrade'

export interface AppRouteParams {
  style: string
  sourceType?: string
  query: string
  projectId: string
  paperId?: string
  citationId?: string
  create?: string
}

export interface NextJsRoute {
  /**
   * NextJS: `href` is understood as a page route
   */
  href: string

  /**
   * NextJS: `as` is understood as a URL that acts as a decoration to `href`
   */
  as: string
}

export interface WebRoute {
  /**
   * This is understood as being a normal URL for use in non-NextJS <a>
   */
  href: string
  as?: undefined | null
}

export type AppRoute = NextJsRoute | WebRoute

export interface AppRouteCreators {
  toCitationsHome: () => AppRoute
  toProject: () => AppRoute
  toProfile: () => AppRoute
  toLogout: () => AppRoute
  toSignup: () => WebRoute
  toUpgrade: () => WebRoute
  toUpgradeCTA: () => WebRoute
  toLogin: () => WebRoute
  toGateway: () => AppRoute
  toPlagiarism: () => WebRoute
  toSourceTypeSelection: (params: Pick<AppRouteParams, 'style'>) => AppRoute
  toSourceSearch: (params: Pick<AppRouteParams, 'style' | 'sourceType'>) => AppRoute
  toSourceSelection: (
    params: Pick<AppRouteParams, 'style' | 'sourceType' | 'query'>
  ) => AppRoute
  toSourceConfirmation: (params: Pick<AppRouteParams, 'style' | 'sourceType'>) => AppRoute
  toNewCitation: (params: Pick<AppRouteParams, 'style' | 'sourceType'>) => AppRoute
  toManualCitation: (params: Pick<AppRouteParams, 'style' | 'sourceType'>) => AppRoute
  toEditCitation: (
    params: Pick<AppRouteParams, 'style' | 'sourceType' | 'citationId'>
  ) => AppRoute
  toBibliography: (params: Pick<AppRouteParams, 'style' | 'projectId'>) => AppRoute
  toFolders: (params?: Pick<AppRouteParams, 'create'>) => AppRoute
  toDashboard: () => AppRoute
  toCitationsDashboard: () => AppRoute
  toCitationList: (params: Pick<AppRouteParams, 'projectId'>) => AppRoute
  toCitationListSource: (params: Pick<AppRouteParams, 'projectId'>) => AppRoute
  toCitationListForm: (
    params: Pick<AppRouteParams, 'projectId' | 'sourceType'>
  ) => AppRoute
  toPapersDashboard: () => AppRoute
  toUploadLp: () => AppRoute
  toError: () => AppRoute
  toTermsAndConditions?: () => WebRoute
  toProofReader?: (params?: Pick<AppRouteParams, 'paperId'>) => AppRoute
}

export type AppRouteName = keyof AppRouteCreators

export interface AppRouteOpts {
  replace?: boolean
  query?: { [key: string]: string }
}

export type MatchPathSegment = (
  pathnameSegment: string
) => { path: string; isExact: boolean; url: string; params: any }

export type GetFromQuery = (
  router: NextRouter,
  query: string,
  wrapByAlias?: boolean
) => string

export type IsExact = (route: string, query: string) => boolean

export interface MetaTag {
  name: string
  content: string
}

export interface HeadTags {
  title: string
  metaTags: MetaTag[]
}

export interface SourceType {
  name: string
  value: string
  manual: boolean
  title: string
  apiAlias: string
  id: string
}

export interface StyleType {
  name: string
  id: string
}

export type JournalType = typeof JOURNAL | typeof JSTOR

export enum SearchWidgetVariations {
  CONTROL = 'control',
  TEST = 'test',
}

export enum AdUnlockVariations {
  CONTROL = 'control',
  VARIANT1 = 'variant1',
  VARIANT2 = 'variant2',
}

export enum CitationValidityVariations {
  ABOVE_VID_AD = 'above_vid_ad',
  BELOW_VID_AD = 'below_vid_ad',
  CONTROL = 'control',
}

export interface AnchorProps {
  children: string
  href: string
}

export interface Resource {
  header: string
  summary: string | JSX.Element
  url?: string
  seoLinks?: AnchorProps[]
  dataTestId: string
}

export interface Partner {
  href: string
  imgFilename: string
  imgAlt: string
  rel: string
  width: number
  height: number
}

export interface Messages {
  [key: string]: {
    title?: string
    description?: string
  }
}

export interface HeaderWithBulletPoints {
  header: string
  body: string[]
}

export interface TwoHeadersAndBulletPoints {
  bodyHeader: string
  header: string
  body?: string[]
}

export interface FormB1 {
  header: string
  table: TwoHeadersAndBulletPoints[]
}

export interface CredibilityBody {
  header?: React.ReactNode
  body: React.ReactNode[]
  isList?: boolean
  tabbed?: boolean
}

export interface Credibility {
  header: React.ReactNode
  body: CredibilityBody[]
}

export interface UpgradeBullet {
  bullet: string
  dataTestId: string
  isFreeTrial?: boolean
}

export interface UpgradeCopy {
  button: string
  header: string | string[]
  body: UpgradeBullet[]
  hideTermsAndConditions?: boolean
}

export interface LocationCopy {
  alt: string
  imageUrl: string
  LocationName: Location
}

export interface HomeCopy {
  header: string
  createCitations: Card
  checkPaper: Card
}

interface CardContext {
  value: string
  contextTestId: string
}

export type CardType = typeof TYPE_CREATE_CITATIONS | typeof TYPE_CHECK_PAPER

export interface Card {
  imgFilename: string
  buttonText: string
  context: CardContext
  dataTestId: string
  type: CardType
}

export interface PlusTabs {
  header: string
  subHeader: string
  tabs: PlusTabInfo[]
}

export type PlusTabType =
  | typeof TYPE_CITATIONS
  | typeof TYPE_PLAGIARISM
  | typeof TYPE_EXPERT_CHECK
  | typeof TYPE_GRAMMAR

export interface PlusTabInfo {
  label: string
  imgFilename: string
  alt: string
  to: string
  header: string
  context: string
  dataTestId: string
  type: PlusTabType
}

export interface Source {
  data: {
    autocite?: {
      url?: string
      search?: string
    }
    contributors?: Contributor[]
    website?: {
      title?: string
      summary?: string
    }
    source?: string
    other?: {
      oclc?: string
    }
    pubonline?: {
      yearaccessed?: string
      monthaccessed?: string
      dayaccessed?: string
      url?: string
      title: string
      inst: string
      day: string
      month: string
      year: string
    }
  }
  display: {
    title?: string
    page_title?: string
    displayurl?: string
    year?: string
    date?: string
    publisher?: string
    url?: string
  }
  link?: string
  feedback?: {}
  autociteRioTrackingID?: string
  autociteResponseForRio?: AutociteMetadataInput
  numFieldsAutocite?: number
}

export interface Contributor {
  last?: string
  first?: string
  middle?: string
  suffix?: string
  function?: string
}

export type NavLocalRoute =
  | 'toProject'
  | 'toBibliography'
  | 'toFolders'
  | 'toLogout'
  | 'toUpgrade'
  | 'toProfile'
  | 'toSignup'
  | 'toLogin'
  | 'toDashboard'
  | 'toSourceTypeSelection'
  | 'toCitationList'
  | 'toPlagiarism'

export interface NavItem {
  name: string
  href?: string
  icon?: string
  hideOnMobile?: boolean
  hideOnSmall?: boolean
  new?: boolean
  route?: NavLocalRoute
  query?: { [key: string]: string }
  target?: string
}

export interface NavItems {
  main: NavItem[]
  right: NavItem[]
  account: NavItem[]
  header?: NavItem[]
}

/** this is a menu option used as a selection for a dropdown, an item in a menu, etc */
export interface Option {
  /** id of option */
  id: string | number
  /** text to display of option */
  name: string
}

export interface LegalContents {
  commitment: string
  copyright: string
  tagline: string
}

export interface FooterLink {
  url: string
  name: string
  rel?: string
  id?: string
  className?: string
}

export interface FooterColumn {
  title: string
  links: FooterLink[]
}

/**
 * Features consist of an id that is used by products to describe
 * feature participation, and a name that will be used to generate
 * the row headers for each feature
 */
type PricingTableFeature = {
  id: string
  name: string
}

/**
 * One of the products to be rendered in the column,
 * with a list of descriptions of feature participation
 * for each product
 */
export type PricingTableProduct = {
  id: string
  name: string
  features: {
    [key: string]: string
  }
  price: string
  recommended?: boolean
  basePrice?: string
}

export interface PricingTable {
  title?: string
  features: PricingTableFeature[]
  products: PricingTableProduct[]
  termsAndConditionsUrl?: string
  studyPackUrl?: string
}

export interface Project extends FetchProjects_projects {
  index?: number
  citationCount?: number
  isLoading?: boolean
  setInProgress?: boolean
  styleName?: string
  urlStyleName?: string
  generateNewCitationList?: boolean
}
